import { BuildWorkflowOptions } from "projen/lib/build";
import { WorkflowSteps } from "projen/lib/github";
import { JobStep } from "projen/lib/github/workflows-model";
import {
  NodePackageManager,
  PnpmOptions,
  UpgradeDependenciesSchedule,
} from "projen/lib/javascript";
import {
  TypeScriptAppProject,
  TypeScriptProjectOptions,
} from "projen/lib/typescript";
import { merge } from "ts-deepmerge";
import {
  formatLayoutViolation,
  formatStarlightSingletonViolation,
  LAYOUT_ENFORCEMENT,
  type LayoutEnforcement,
  validateMonorepoLayout,
  validateStarlightSingleton,
} from "./monorepo-layout";
import { Nvmrc } from "./nvmrc";
import { applyPackagePrivate, PackagePrivateOptions } from "./package-private";
import { ProjectMetadata } from "./project-metadata";
import { ProjectMetadataOptions } from "./project-metadata-options";
import { AgentConfig } from "../agent/agent-config";
import { AgentConfigOptions } from "../agent/agent-config-options";
import { PnpmWorkspace, PnpmWorkspaceOptions } from "../pnpm/pnpm-workspace";
import { ResetTask, ResetTaskOptions } from "../tasks/reset-task";
import {
  ROOT_CI_TASK_NAME,
  TurboRepo,
  TurboRepoOptions,
} from "../turbo/turbo-repo";
import { VERSION } from "../versions";
import { VSCodeConfig } from "../vscode/vscode";
import {
  addApproveMergeUpgradeWorkflow,
  type ApproveMergeUpgradeOptions,
} from "../workflows/approve-merge-upgrade";
import { addBuildCompleteJob } from "../workflows/build-complete-job";
import { includeHiddenFilesInBuildArtifact } from "../workflows/include-hidden-files-in-build-artifact";
import { pinPnpmActionSetup } from "../workflows/pin-pnpm-action-setup";
import { pinSetupNodeVersion } from "../workflows/pin-setup-node-version";
import {
  RequirementIssueTemplate,
  type RequirementIssueTemplateOptions,
} from "../workflows/requirement-issue-template";
import {
  addSyncLabelsWorkflow,
  type SyncLabelsOptions,
} from "../workflows/sync-labels";

const CONFIGULATOR_PACKAGE_NAME = "@codedrifters/configulator";

/**
 * Step-level env that opts an upgrade-workflow step out of CI mode.
 *
 * GitHub Actions always sets `CI=true`, which makes projen's
 * `NodePackage.postSynthesize` pick the **frozen** `install:ci` task
 * (`pnpm i --frozen-lockfile`) for its synth-time install. That is wrong for
 * a step whose whole job is to move dependencies forward: synth rewrites
 * `pnpm-workspace.yaml`'s `catalog:` from `versions.ts`, so the freshly
 * written workspace no longer matches the lockfile's `catalogs` block and the
 * frozen install dies with `ERR_PNPM_LOCKFILE_CONFIG_MISMATCH`. The upgrade
 * job then produces no patch, the consumer's lockfile never advances, and
 * every subsequent scheduled run re-hits the same wall with a larger gap.
 *
 * The value must be the literal string `"false"`, **not** `"0"`:
 *
 * - projen's `isTruthy` treats both `"0"` and `"false"` as falsy, so either
 *   would satisfy projen's `isAutomatedBuild` check on its own.
 * - pnpm's CI detection (ci-info) bypasses **only** on `env.CI !== "false"` —
 *   the string `"0"` is truthy there. With `CI=0`, pnpm's pre-exec
 *   `verifyDepsBeforeRun` deps-status check still spawns a *frozen*
 *   `pnpm install` before projen even starts, and fails identically.
 *
 * `"false"` is the only value that satisfies both tools. Do not "simplify" it
 * to `"0"` — projen's own `upgrade` task uses `CI: "0"`, but it does not have
 * to survive pnpm's pre-run deps check the way these steps do.
 *
 * Applied at **step** level rather than job level to keep the override
 * surgical: only the steps that legitimately mutate the lockfile opt out of
 * CI mode, while the projen-generated steps around them keep Actions'
 * default `CI=true`.
 *
 * `Run subproject upgrades` needs this as much as `Synthesize` does — it is
 * inserted for consumers and non-consumers alike, and pnpm's pre-run deps
 * check re-runs a frozen install there whenever the lockfile is still stale.
 *
 * This complements — and does not replace — `installDependenciesNonFrozen()`,
 * which covers the other synth-time install path (see #570).
 *
 * @see https://github.com/codedrifters/packages/issues/1006
 * @see https://github.com/codedrifters/packages/issues/570
 */
const NON_CI_STEP_ENV: Record<string, string> = { CI: "false" };

/*******************************************************************************
 *
 * Monorepo Root Project.
 *
 * This project should be used as the base project for other projects in a
 * monorepo. The Monorepo root project generally won't contain any code, but it
 * will contain configuration for the monorepo, such as package management,
 * linting, testing, and other project-wide settings.
 *
 ******************************************************************************/

export interface IDependencyResolver {
  resolveDepsAndWritePackageJson(): boolean;
}

/**
 * Consolidated PNPM options for the monorepo.
 *
 * Extends projen's native {@link PnpmOptions} (so projen's `workspaceYamlOptions`
 * stays available and type-compatible with the inherited
 * `TypeScriptProjectOptions.pnpmOptions`) and adds configulator's
 * monorepo-specific controls. Configulator's `PnpmWorkspace` component remains
 * the single writer of `pnpm-workspace.yaml`; see `src/pnpm/pnpm-workspace.ts`.
 */
export interface MonorepoPnpmOptions extends PnpmOptions {
  /**
   * Options for the monorepo workspace file (`pnpm-workspace.yaml`).
   */
  pnpmWorkspaceOptions?: PnpmWorkspaceOptions;
}

/**
 * Configuration options for the monorepo.
 */
export interface MonorepoProjectOptions
  extends
    Omit<TypeScriptProjectOptions, "defaultReleaseBranch">,
    PackagePrivateOptions {
  /**
   * Turn on Turborepo support.
   *
   * @default true
   */
  turbo?: boolean;

  /**
   * Optionsal options for turborepo config
   */
  turboOptions?: TurboRepoOptions;

  /**
   * Steps to run in `build.yml` after the projen build, replacing the default
   * "Build Sub Projects" step that runs `pnpm exec projen build:all`.
   *
   * When omitted, behavior is unchanged: a single step runs the root
   * `build:all` task. Useful for repos whose CI wants a cheaper PR-time
   * check than the full turbo build (e.g. an Astro `sync` + `check`).
   *
   * The follow-on `Upload Turbo runs` artifact step is always emitted and
   * is a no-op when `.turbo/runs` is absent.
   *
   * @default - one step: `pnpm exec projen build:all`
   */
  readonly ciBuildSteps?: Array<JobStep>;

  /**
   * Enable the reset task that deletes all build artifacts.
   *
   * @default true
   */
  resetTask?: boolean;

  /**
   * Options for the reset task.
   */
  resetTaskOptions?: ResetTaskOptions;

  /**
   * PNPM options for the monorepo.
   */
  pnpmOptions?: MonorepoPnpmOptions;

  /**
   * When set, adds the approve-and-merge-upgrade workflow (ADR 0001 §2).
   * Uses a second GitHub App to approve and merge the dependency-upgrade PR
   * when it has the auto-approve label and head branch matches. Trigger types
   * exclude `opened` to prevent double approval.
   *
   * @see ApproveMergeUpgradeOptions
   */
  readonly approveMergeUpgradeOptions?: ApproveMergeUpgradeOptions;

  /**
   * Whether this monorepo consumes `@codedrifters/configulator` from a registry (npm).
   * When true, the upgrade workflow adds steps to sync configulator and synthesize.
   * Set to false when configulator is developed in this repo (e.g. workspace dependency).
   *
   * @default true
   */
  readonly configulatorRegistryConsumer?: boolean;

  /**
   * Project metadata configuration. Provides repository identity,
   * GitHub project context, and organizational details consumed by
   * AgentConfig and other features at synthesis time.
   *
   * - `undefined` or `{}`: auto-instantiate with auto-detection (default)
   * - `ProjectMetadataOptions`: explicit metadata configuration
   * - `false`: disable ProjectMetadata entirely
   *
   * @default `{}` (auto-detect from package.json)
   */
  readonly projectMetadata?: ProjectMetadataOptions | false;

  /**
   * AI agent configuration (Cursor, Claude Code rules).
   * Generates rule files at the monorepo root with auto-detected bundles
   * based on project and subproject introspection.
   *
   * - `true` or `{}`: enable with auto-detected defaults
   * - `AgentConfigOptions`: enable with explicit configuration
   * - `false` or `undefined`: disabled (default)
   *
   * @default false
   */
  readonly agentConfig?: AgentConfigOptions | boolean;

  /**
   * Sync GitHub labels from a config file using EndBug/label-sync.
   * Generates `.github/labels.yml` and a sync workflow.
   *
   * - `true` or `undefined`: enable with default priority labels (default)
   * - `SyncLabelsOptions`: enable with custom label configuration
   * - `false`: disable label syncing
   *
   * @default true
   */
  readonly syncLabels?: SyncLabelsOptions | boolean;

  /**
   * Emit a GitHub markdown issue template
   * (`.github/ISSUE_TEMPLATE/requirement-impl.md`) carrying the canonical
   * cross-repo `## Requirement` traceability block (ID, spec URL, tier,
   * customer) plus a prompt to add a `req:<ID>` label.
   *
   * Opt-in — **off by default**. Enable it on code repositories whose
   * issues trace back to canonical requirements maintained in a separate
   * planning repo, so a human filing an implementation issue records the
   * same requirement metadata a fan-out bot would. Repos with no
   * requirement-traceability story leave it off and emit no template.
   *
   * - `true`: enable with defaults (template at the default path,
   *   `type:feat` front-matter label).
   * - `RequirementIssueTemplateOptions`: enable with a custom output path
   *   and/or front-matter labels.
   * - `false` or `undefined`: disabled (default) — no issue template file
   *   is emitted.
   *
   * @default false
   * @see docs/src/content/docs/packages/\@codedrifters/configulator/requirement-traceability.md
   */
  readonly requirementIssueTemplate?: RequirementIssueTemplateOptions | boolean;

  /**
   * How to react when a sub-project's `outdir` falls outside the root
   * expected for its project type (ADR-006 §4).
   *
   * - `"off"` — skip validation entirely.
   * - `"warn"` — log a console warning per violation; synth proceeds.
   * - `"error"` — throw on the first violation; synth fails.
   *
   * Defaults to `"warn"` during the ADR-006 rollout so existing consumers
   * with legacy outdirs see actionable warnings but do not break. A future
   * release will flip the default to `"error"`.
   *
   * @default "warn"
   * @see docs/src/content/docs/requirements/architectural-decisions/ADR-006-monorepo-layout.md
   */
  readonly layoutEnforcement?: LayoutEnforcement;
}

// Intersection (not `extends`) so the `pnpmOptions` types from projen and
// configulator merge coherently: `PnpmOptions & MonorepoPnpmOptions` resolves
// to `MonorepoPnpmOptions` (which extends `PnpmOptions`) rather than tripping
// the "not identical" interface-extension error.
type AppliedOptions = TypeScriptProjectOptions & MonorepoProjectOptions;

/**
 * Per-instance list of post-install dependency resolvers. Stored in a WeakMap
 * so MonorepoProject has no private instance property and its type remains
 * structural across package versions (avoids TS2322 when two configulator
 * versions are in the dependency tree).
 */
const postInstallDependenciesMap = new WeakMap<
  MonorepoProject,
  Array<() => boolean>
>();

export class MonorepoProject extends TypeScriptAppProject {
  /**
   * Version of PNPM which the whole monorepo should use.
   */
  readonly pnpmVersion: string;

  /**
   * Whether this monorepo consumes configulator from a registry (drives upgrade workflow steps).
   */
  readonly configulatorRegistryConsumer: boolean;

  /**
   * Layout-enforcement mode for sub-project `outdir` validation (ADR-006).
   */
  readonly layoutEnforcement: LayoutEnforcement;

  constructor(userOptions: MonorepoProjectOptions) {
    /***************************************************************************
     *
     * BUILD WORKFLOW OPTIONS
     *
     * discover some turbo options for build workflow, if needed.
     *
     **************************************************************************/

    const buildWorkflowOptions: Partial<BuildWorkflowOptions> = userOptions
      .turboOptions?.remoteCacheOptions
      ? TurboRepo.buildWorkflowOptions(
          userOptions.turboOptions.remoteCacheOptions,
        )
      : {};

    /***************************************************************************
     *
     * DEFAULT OPTIONS
     *
     * These are the default options unless you override with option inputs.
     *
     **************************************************************************/

    const defaultOptions: Omit<
      MonorepoProjectOptions,
      "name" | "defaultReleaseBranch"
    > = {
      /**
       * Use typescript based config file.
       */
      projenrcTs: true,

      /**
       * Projen version should be pinned to the local specified version.
       */
      projenVersion: "catalog:",

      /**
       * Use Prettier for code formatting.
       */
      prettier: true,

      /**
       * Not licensed by default.
       */
      licensed: false,

      /**
       * GitHub options for the monorepo.
       * Don't enable mergify by default.
       */
      githubOptions: {
        mergify: false,

        /**
         * Configure pull request linting to validate PR titles follow Conventional Commits.
         * By default, all conventional commit types are allowed, providing flexibility
         * for different types of changes (features, fixes, documentation, refactoring, etc.).
         */
        pullRequestLintOptions: {
          semanticTitleOptions: {
            /**
             * Allowed conventional commit types for PR titles.
             * This includes all standard types from the Conventional Commits specification:
             * - feat: New features
             * - fix: Bug fixes
             * - docs: Documentation changes
             * - style: Code style changes (formatting, etc.)
             * - refactor: Code refactoring
             * - perf: Performance improvements
             * - test: Test additions or changes
             * - build: Build system changes
             * - ci: CI configuration changes
             * - chore: Maintenance tasks
             * - revert: Revert commits
             */
            types: [
              "feat",
              "fix",
              "docs",
              "style",
              "refactor",
              "perf",
              "test",
              "build",
              "ci",
              "chore",
              "revert",
            ],
          },
        },
      },

      /**
       * Default PNPM version to use in the monorepo.
       */
      pnpmVersion: VERSION.PNPM_VERSION,

      /**
       * Node.js version used in GitHub Actions workflows (build, upgrade, etc.).
       * Pinning this inserts an `actions/setup-node` step before dependency
       * install so CI does not fall back to whatever Node the `ubuntu-latest`
       * runner ships with (currently Node 20.x, which is below the floor
       * required by Astro 6 / Starlight).
       *
       * @see {@link VERSION.NODE_WORKFLOWS}
       */
      workflowNodeVersion: VERSION.NODE_WORKFLOWS,

      /**
       * By default treat as a registry consumer (upgrade workflow syncs configulator).
       */
      configulatorRegistryConsumer: true,

      /**
       * ADR-006 layout enforcement defaults to "warn" during rollout so
       * legacy outdirs log actionable warnings but do not break synth.
       */
      layoutEnforcement: LAYOUT_ENFORCEMENT.WARN,

      /**
       * We don't want sample code generated for the root project.
       */
      sampleCode: false,

      /**
       * Jest is not required in the root project.
       */
      jest: false,

      /**
       * Don't release the root project.
       */
      release: false,

      /**
       * Uppgrade dependencies automatically unless otherwise instructed.
       * Exclude `@codedrifters/configulator` so the default upgrade task does not
       * upgrade it (version is often managed in-repo or via a separate sync step).
       */
      depsUpgrade: true,
      depsUpgradeOptions: {
        exclude: ["@codedrifters/configulator"],
        workflowOptions: {
          schedule: UpgradeDependenciesSchedule.DAILY,
        },
        cooldown: 7,
      },

      /**
       * Disable tsconfig.dev.json in the root since we aren't going to be
       * developing any code here. It's just a task runner and configuration
       * tool for sub-projects.
       */
      disableTsconfigDev: true,

      /**
       * Kill the srcdir in the root since we aren't using one. Projen's
       * default `tsconfig.include` still points at `src/**\/*.ts`, so
       * callers that want to treat the monorepo root as a pure shell
       * should see an empty include — consumers add their own entries via
       * `tsconfig.addInclude(...)` (e.g. `.projenrc.ts`, `projenrc/**\/*.ts`).
       */
      tsconfig: {
        compilerOptions: {
          rootDir: undefined,
        },
        exclude: ["node_modules"],
      },

      /**
       * Enable turborepo by default.
       */
      turbo: true,

      /**
       * Enable reset task by default.
       */
      resetTask: true,

      /**
       * Include self as a devDep
       */
      devDeps: ["@codedrifters/configulator"],

      /**
       * PNPM options for the monorepo.
       */
      pnpmOptions: {
        pnpmWorkspaceOptions: {
          defaultCatalog: {
            ["@types/node"]: VERSION.TYPES_NODE_VERSION,
            ["aws-cdk"]: VERSION.AWS_CDK_CLI_VERSION,
            ["aws-cdk-lib"]: VERSION.AWS_CDK_LIB_VERSION,
            ["projen"]: VERSION.PROJEN_VERSION,
            ["constructs"]: VERSION.AWS_CONSTRUCTS_VERSION,
            ["turbo"]: VERSION.TURBO_VERSION,
            /**
             * `@vitest/coverage-v8` declares an exact `vitest` peer
             * dependency, so both track one constant.
             */
            ["vitest"]: VERSION.VITEST_VERSION,
            ["@vitest/coverage-v8"]: VERSION.VITEST_VERSION,
          },
        },
      },
    };

    /***************************************************************************
     *
     * REQUIRED OPTIONS
     *
     * These options cannot be changed by the user.
     *
     **************************************************************************/

    const requiredOptions: Omit<TypeScriptProjectOptions, "name"> = {
      /**
       * This is required because it's standard practice and also to simplify
       * some workflow design.
       */
      defaultReleaseBranch: "main",

      /**
       * Use PNPM instead of the default (Yarn). Much of the ecosystem depends
       * on PNPM and making monorepos PNPM only simplifies many configurations.
       */
      packageManager: NodePackageManager.PNPM,

      /**
       * Some additional pre-build steps if we're using turbo's remote cache.
       * Set GIT_BRANCH_NAME so Turborepo remote cache hashes match between local and CI.
       */
      buildWorkflowOptions: {
        env: {
          GIT_BRANCH_NAME: "${{ github.head_ref || github.ref_name }}",
          ...buildWorkflowOptions?.env,
          ...userOptions.buildWorkflowOptions?.env,
        },
        permissions: {
          ...buildWorkflowOptions?.permissions,
          ...userOptions.buildWorkflowOptions?.permissions,
        },
        preBuildSteps: [
          ...(buildWorkflowOptions?.preBuildSteps ?? []),
          ...(userOptions.buildWorkflowOptions?.preBuildSteps ?? []),
        ],
      },
    };

    /***************************************************************************
     *
     * CONSTRUCTOR
     *
     * Combines default options with user provided options and required options.
     * Store the options in a const so we can use them after super(), farther
     * into the constructor for additional configuration.
     *
     **************************************************************************/

    const options: AppliedOptions = merge(
      defaultOptions,
      userOptions,
      requiredOptions,
    );

    super({ ...options });

    postInstallDependenciesMap.set(this, []);

    /**
     * Projen's TypeScriptProject seeds `tsconfig.include` with
     * `<srcdir>/**\/*.ts` by default. MonorepoProject is a shell with no
     * srcdir, so drop that entry — consumers add their own includes (e.g.
     * `.projenrc.ts`, `projenrc/**\/*.ts`) via `this.tsconfig?.addInclude`.
     */
    this.tsconfig?.removeInclude(`${this.srcdir}/**/*.ts`);

    /**
     * Drop the root `tsconfig.tsbuildinfo` before every compile (#656).
     *
     * The root compiles with `tsc --build`, whose incremental
     * `.tsbuildinfo` survives dependency bumps. When a dependency ships a
     * regressed `.d.ts` rollup, a stale buildinfo lets the root's type-check
     * pass locally against the old signatures while cold CI (no buildinfo)
     * fails — the failure only surfaces after merge. Deleting the buildinfo
     * up front forces `tsc --build` to re-check against the current
     * `node_modules` on every compile. The root compiles only projenrc, so
     * the lost incrementality is negligible.
     *
     * Interpreter-safe form: this runs under projen's built-in cross-platform
     * shell (no command substitution), matching the guarded-`rm` idiom in
     * `src/tasks/reset-task.ts`.
     */
    this.preCompileTask.exec(
      '[ -e "tsconfig.tsbuildinfo" ] && rm -f tsconfig.tsbuildinfo || true',
    );

    /***************************************************************************
     *
     * PUBLIC PROPS
     *
     * Some props are hidden by Projen and we need to expose them for the
     * monorepo to work properly. This is where we store them.
     *
     **************************************************************************/

    this.pnpmVersion = options.pnpmVersion!;
    this.configulatorRegistryConsumer =
      options.configulatorRegistryConsumer ?? true;
    this.layoutEnforcement =
      options.layoutEnforcement ?? LAYOUT_ENFORCEMENT.WARN;

    /***************************************************************************
     *
     * CUSTOM CONFIGS
     *
     * We add some additional configurations to the monorepo root project below
     * such as Turborepo, VS Code config, and the PNPM workspace file.
     *
     **************************************************************************/

    /**
     * Add VSCode configuration
     */
    new VSCodeConfig(this);

    /**
     * Emit `.nvmrc` at the monorepo root so local dev installs the
     * same Node version every CI workflow uses
     * ({@link VERSION.NODE_WORKFLOWS}).
     */
    new Nvmrc(this);

    /**
     * Add workspace definition to PNPM root
     */
    new PnpmWorkspace(this, options.pnpmOptions?.pnpmWorkspaceOptions);

    /**
     * Turn on turborepo support if requested.
     */
    if (options.turbo) {
      new TurboRepo(this, options.turboOptions);

      const defaultBuildSubProjectsStep: JobStep = {
        name: "Build Sub Projects",
        run: `pnpm exec projen ${ROOT_CI_TASK_NAME}`,
      };
      const buildSubProjectsSteps: Array<JobStep> = options.ciBuildSteps ?? [
        defaultBuildSubProjectsStep,
      ];

      this.buildWorkflow?.addPostBuildSteps(
        ...buildSubProjectsSteps,
        WorkflowSteps.uploadArtifact({
          name: "Upload Turbo runs",
          if: "always()",
          continueOnError: true,
          with: {
            name: "turbo-runs",
            path: ".turbo/runs",
          },
        }),
      );
    }

    /**
     * Add reset task if enabled.
     */
    if (options.resetTask !== false) {
      const defaultResetTaskOptions: ResetTaskOptions = {
        pathsToRemove: [
          "node_modules",
          ".turbo",
          "dist",
          "lib",
          // Root reset must also drop the incremental buildinfo (#656) so a
          // stale `.tsbuildinfo` can't hide a regressed dependency `.d.ts`
          // rollup from the next compile. Sub-projects already clean this.
          "tsconfig.tsbuildinfo",
        ],
      };
      const userResetTaskOptions = options.resetTaskOptions ?? {};
      const resetTaskOptions: ResetTaskOptions = merge(
        defaultResetTaskOptions,
        {
          ...userResetTaskOptions,
          pathsToRemove: [
            ...(defaultResetTaskOptions.pathsToRemove ?? []),
            ...(userResetTaskOptions.pathsToRemove ?? []),
          ],
        },
      );
      new ResetTask(this, resetTaskOptions);
    }

    /**
     * Specify package manager
     */
    // const pnpmVersion = options.pnpmVersion ?? VERSION.PNPM_VERSION;
    this.package.file.addOverride(
      "packageManager",
      `pnpm@${options.pnpmVersion}`,
    );

    /**
     * The monorepo root is a task-runner shell that is never published, so
     * it renders `"private": true` by default. Pass `packagePrivate: false`
     * to opt out.
     */
    applyPackagePrivate(this, options);

    /**
     * Add some silly things to the gitignore.
     */
    this.gitignore?.addPatterns(".DS_Store", "test-reports");

    /**
     * Use catalog versions for constructs and `@types/node`
     */
    this.addDevDeps("constructs@catalog:", "@types/node@catalog:");

    if (options.approveMergeUpgradeOptions) {
      addApproveMergeUpgradeWorkflow(this, options.approveMergeUpgradeOptions);
    }

    /**
     * Project metadata — auto-instantiated unless explicitly disabled.
     */
    if (options.projectMetadata !== false) {
      new ProjectMetadata(
        this,
        typeof options.projectMetadata === "object"
          ? options.projectMetadata
          : {},
      );
    }

    /**
     * Enable AI agent configuration if requested.
     */
    if (options.agentConfig) {
      new AgentConfig(
        this,
        typeof options.agentConfig === "object" ? options.agentConfig : {},
      );
    }

    /**
     * Sync GitHub labels if enabled. When AgentConfig is present, its
     * active bundles contribute labels to `.github/labels.yml` so a
     * bundle can ship the labels its rules depend on.
     */
    if (options.syncLabels !== false) {
      const syncLabelsOptions =
        typeof options.syncLabels === "object" ? options.syncLabels : {};
      const agentConfig = AgentConfig.of(this);
      addSyncLabelsWorkflow(this, {
        ...syncLabelsOptions,
        bundles: syncLabelsOptions.bundles ?? agentConfig?.activeBundles,
      });
    }

    /**
     * Emit the cross-repo requirement issue template when opted in.
     * Off by default so repos with no requirement-traceability story
     * ship no `.github/ISSUE_TEMPLATE/` file.
     */
    if (options.requirementIssueTemplate) {
      new RequirementIssueTemplate(
        this,
        typeof options.requirementIssueTemplate === "object"
          ? options.requirementIssueTemplate
          : {},
      );
    }

    /**
     * Append the "complete" gate job to the build workflow (ADR 0004). Requires
     * build to succeed and all other jobs to succeed or be skipped.
     */
    if (this.buildWorkflow) {
      addBuildCompleteJob(this.buildWorkflow);
    }

    // Mutate the upgrade workflow job: add subproject upgrade step (always), and
    // for consumers add sync + synthesize before it. addPostBuildSteps runs too
    // late because the workflow is created in UpgradeDependencies constructor.
    const upgradeWorkflow = this.github?.tryFindWorkflow("upgrade");
    const upgradeJob = upgradeWorkflow?.getJob("upgrade");
    const jobWithSteps =
      upgradeJob && "steps" in upgradeJob ? upgradeJob : undefined;
    if (jobWithSteps?.steps && Array.isArray(jobWithSteps.steps)) {
      // Insert *after* the projen-generated "Upgrade dependencies" step.
      // Looking it up by name keeps us resilient to upstream step shuffles
      // (e.g. Projen inserting a `Setup Node.js` step when
      // `workflowNodeVersion` is set).
      const upgradeStepIndex = jobWithSteps.steps.findIndex(
        (step) => step.name === "Upgrade dependencies",
      );
      const insertIndex =
        upgradeStepIndex >= 0
          ? upgradeStepIndex + 1
          : jobWithSteps.steps.length;
      if (this.configulatorRegistryConsumer) {
        jobWithSteps.steps.splice(insertIndex, 0, {
          name: `Sync ${CONFIGULATOR_PACKAGE_NAME} in workspace`,
          run: `pnpm update -r ${CONFIGULATOR_PACKAGE_NAME}`,
        });
        // `env: { CI: "false" }` (NOT "0") — this step rewrites
        // pnpm-workspace.yaml's catalog, so it must not run projen's frozen
        // install:ci. See NON_CI_STEP_ENV above for why "0" is insufficient
        // (#570, #1006). Spread so the two steps do not alias one object.
        jobWithSteps.steps.splice(insertIndex + 1, 0, {
          name: "Synthesize",
          run: this.runTaskCommand(this.defaultTask!),
          env: { ...NON_CI_STEP_ENV },
        });
      }
      // Inserted for consumers and non-consumers alike, and it needs the same
      // non-CI env: pnpm's pre-run deps check re-runs a frozen install here
      // whenever the lockfile is still stale (#570, #1006).
      jobWithSteps.steps.splice(
        insertIndex + (this.configulatorRegistryConsumer ? 2 : 0),
        0,
        {
          name: "Run subproject upgrades",
          run: "pnpm -r run upgrade",
          env: { ...NON_CI_STEP_ENV },
        },
      );
    }
  }

  /**
   * Validate sub-project `outdir` values against the ADR-006 monorepo
   * layout contract. Runs in `preSynthesize` so all sub-projects are
   * attached by the time we inspect the tree.
   *
   * Two checks run in sequence:
   *
   * 1. **Outdir-vs-root validation.** Every sub-project's `outdir` must
   *    live under the root expected for its project type (ADR-006 §4).
   * 2. **Docs-singleton validation.** At most one `StarlightProject`
   *    may carry `role: "docs"` (ADR-006 §3).
   *
   * Behavior is controlled by `layoutEnforcement`:
   * - `"off"`  — skip both checks.
   * - `"warn"` — log a `console.warn` per violation; continue. Singleton
   *   violations also warn in this mode rather than throwing, so all
   *   layout findings are surfaced consistently.
   * - `"error"` — throw on the first outdir violation, or on the
   *   singleton violation if outdir validation passed.
   */
  public preSynthesize() {
    super.preSynthesize();

    /**
     * Pin every `pnpm/action-setup` step across every workflow on this
     * project's GitHub component to {@link VERSION.PNPM_ACTION_SETUP_VERSION}.
     * Projen's NodeProject and JsiiProject hardcode their own pin in the
     * generated build / release / upgrade workflows; without this patch,
     * the action installs the wrong pnpm version and CI breaks (e.g.
     * v5 cannot install pnpm 11).
     */
    pinPnpmActionSetup(this);

    /**
     * Pin every `actions/setup-node` step's `node-version` input across
     * every workflow on this project's GitHub component to
     * {@link VERSION.NODE_WORKFLOWS}. Projen's release workflow generator
     * hardcodes `node-version: "lts/*"`; without this patch, release
     * workflows install whatever Node lts/* resolves to while build /
     * upgrade workflows install the centralised constant. One source of
     * truth keeps every workflow in sync.
     */
    pinSetupNodeVersion(this);

    /**
     * Set `include-hidden-files: true` on the projen-emitted
     * `Upload artifact` step in the build job (the one that uploads
     * `dist`). Projen brackets that upload with
     * `Backup artifact permissions` / `Restore build artifact permissions`
     * steps that capture and restore ACLs for **every** entry under `dist/`,
     * including dotfiles. `actions/upload-artifact` v4+ defaults
     * `include-hidden-files` to `false`, so without this patch every
     * hidden entry is silently dropped from the artifact and the
     * `setfacl --restore` step in each downstream deploy job exits
     * non-zero, producing a noisy failure annotation per deploy job.
     */
    includeHiddenFilesInBuildArtifact(this);

    if (this.layoutEnforcement === LAYOUT_ENFORCEMENT.OFF) {
      return;
    }

    const violations = validateMonorepoLayout(this);
    const singletonViolation = validateStarlightSingleton(this);

    if (this.layoutEnforcement === LAYOUT_ENFORCEMENT.ERROR) {
      if (violations.length > 0) {
        throw new Error(formatLayoutViolation(violations[0]));
      }
      if (singletonViolation !== undefined) {
        throw new Error(formatStarlightSingletonViolation(singletonViolation));
      }
      return;
    }

    // "warn"
    for (const violation of violations) {
      console.warn(formatLayoutViolation(violation));
    }
    if (singletonViolation !== undefined) {
      console.warn(formatStarlightSingletonViolation(singletonViolation));
    }
  }

  /**
   * Allows a sub project to request installation of dependency at the Monorepo root
   * They must provide a function that is executed after dependencies have been installed
   * If this function returns true, the install command is run for a second time after all sub project requests have run.
   * This is used to resolve dependency versions from `*` to a concrete version constraint.
   */
  public requestInstallDependencies(resolver: IDependencyResolver) {
    postInstallDependenciesMap
      .get(this)!
      .push(resolver.resolveDepsAndWritePackageJson);
  }

  /**
   * Runs a non-frozen dependency install during synth.
   *
   * Projen's `NodePackage.installDependencies` runs the frozen `install:ci`
   * task (`pnpm i --frozen-lockfile`) whenever `process.env.CI` is truthy.
   * That is wrong for a synth-time install: synth itself can rewrite
   * `pnpm-workspace.yaml` (e.g. when a configulator catalog value in
   * `versions.ts` bumps), leaving the lockfile out of sync with the freshly
   * written workspace catalog. A frozen install then fails with
   * `ERR_PNPM_LOCKFILE_CONFIG_MISMATCH`, which previously broke the scheduled
   * upgrade workflow on every consumer repo across any catalog bump.
   *
   * Frozen-install enforcement belongs to CI's explicit `install:ci`
   * invocation, not to the synth-time install. We therefore always run the
   * non-frozen `install` task here, which refreshes the lockfile to match the
   * rewritten workspace — matching the canonical regen sequence consumers
   * already document (`pnpm i && pnpm exec projen && pnpm i`, neither frozen).
   *
   * Runs the task via `this.tasks.runTask` (the public task-execution API,
   * which projen itself uses in `NodePackage.installDependencies`) rather than
   * constructing a `TaskRuntime`: projen 0.100 removed `TaskRuntime` from the
   * package root and made it internal to the CLI. `Tasks.runTask` is stable
   * across the 0.99/0.100 boundary and executes against this project's outdir,
   * so behavior is unchanged.
   *
   * @see https://github.com/codedrifters/packages/issues/570
   */
  private installDependenciesNonFrozen() {
    this.tasks.runTask(this.package.installTask.name);
  }

  /**
   * Hooks into the install dependencies cycle
   */
  public postSynthesize() {
    const postInstallDependencies = postInstallDependenciesMap.get(this);
    if (postInstallDependencies?.length) {
      this.installDependenciesNonFrozen();

      const completedRequests = postInstallDependencies.map((request) =>
        request(),
      );
      if (completedRequests.some(Boolean)) {
        this.installDependenciesNonFrozen();
      }

      postInstallDependenciesMap.set(this, []);
    }
  }
}
